import type { CubejsApi } from '@cubejs-client/core';
import { omit } from 'lodash-es';
import { buildFilterOptionsQuery } from '@/api/queries/filter/buildFilterOptionsQuery';
import { mapDynamicFilterOptions } from '@/api/queries/filter/mapDynamicFilterOptions';
import { useOptionScopeStore } from '@/api/queries/filter/optionScope';
import { isStringArray, transformResult } from '@/api/queries/utils';
import type { QueryResult } from '@/api/queries/useQuery';
import type { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { FILTER_CONFIG } from '@/models/filter/filterConfig';
import { FilterType } from '@/models/filter/FilterType';
import { primeUserCountriesCache, syncCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { collectOptionPathsForSelection } from '@/models/filter/utils/collectOptionPathsForSelection';
import { useLocalOptionPathsStore } from '@/models/filter/utils/localOptionPathsStore';
import type { UserInfo } from '@/types/auth';
import { isDynamicFilter, type FilterConfigDynamic } from '@/types/filter/filterConfig';
import type { FilterState } from '@/types/filter/filterState';
import type { SelectItem } from '@/types/ui/selectItem';
import { sanitizeCubeQuery } from '@/utils/cube/sanitizeCubeQuery';
import { scopeFiltersToCube } from '@/utils/cube/scopeFiltersToCube';
import { buildGlobalFilters } from '@/utils/cube/useGetGlobalFilters';
import { isPeriod } from '@/utils/date/isPeriod';

interface ReconstructOptionPathsParams {
  cubejsApi: CubejsApi;
  userInfo: UserInfo | null;
  userCountries: SelectItem<string>[] | undefined;
  candidateState: FilterState;
  optionPaths: OptionPathsByFilter | undefined;
}

interface FetchPathsParams extends Omit<ReconstructOptionPathsParams, 'optionPaths'> {
  type: FilterType;
  config: FilterConfigDynamic;
  selectedPaths: string[];
}

const isFilterType = (key: string): key is FilterType => key in FILTER_CONFIG;

const fetchOptionPathsForSelection = async ({
  cubejsApi,
  userInfo,
  candidateState,
  type,
  config,
  selectedPaths,
}: FetchPathsParams): Promise<string[]> => {
  const globalFilters = buildGlobalFilters(omit(candidateState, type), {
    availableValuesByFilter: {},
    optionPathsByFilter: {},
    localOptionPathsByFilter: useLocalOptionPathsStore.getState().pathsByFilter,
  });
  const query = buildFilterOptionsQuery(
    config,
    candidateState[FilterType.DATE_RANGE],
    useOptionScopeStore.getState().filters,
    (config.applyDynamicFilters ?? true) ? scopeFiltersToCube(globalFilters, config.sourceCube) : null,
    isPeriod(candidateState[FilterType.COMPARISON_PERIOD])
      ? candidateState[FilterType.COMPARISON_PERIOD]
      : undefined,
  );
  if (!query) throw new Error(`Cannot build option query for ${type}`);

  const result: QueryResult = await cubejsApi.load(sanitizeCubeQuery(query, true));
  const options = mapDynamicFilterOptions(config, transformResult(result, false), userInfo ?? undefined);
  const allPaths: string[] = [];
  for (const option of options) {
    if (typeof option.value === 'string') allPaths.push(option.value);
  }
  if (allPaths.length === 0) throw new Error(`Empty option universe for ${type}`);

  const paths = collectOptionPathsForSelection(allPaths, selectedPaths);
  if (paths.length === 0) throw new Error(`No option paths resolved for ${type}`);
  return paths;
};

export const reconstructMissingOptionPaths = async ({
  optionPaths,
  ...params
}: ReconstructOptionPathsParams): Promise<OptionPathsByFilter | undefined> => {
  const pending = Object.keys(FILTER_CONFIG)
    .filter(isFilterType)
    .flatMap(type => {
      const config = FILTER_CONFIG[type];
      const value = params.candidateState[type];
      if (!isDynamicFilter(config) || !config.usesOptionPaths) return [];
      if (!isStringArray(value) || value.length === 0) return [];
      if (optionPaths?.[type]?.length) return [];
      return [{ type, config, selectedPaths: value }];
    });
  if (pending.length === 0) return optionPaths;

  primeUserCountriesCache(params.userCountries);
  const countryValue = params.candidateState[FilterType.COUNTRY];
  if (isStringArray(countryValue) && countryValue.length) {
    syncCachedCountryCodes(countryValue);
  }

  const fetched = await Promise.all(
    pending.map(async selection => ({
      type: selection.type,
      paths: await fetchOptionPathsForSelection({ ...params, ...selection }),
    })),
  );
  const resolved: OptionPathsByFilter = { ...optionPaths };
  for (const { type, paths } of fetched) resolved[type] = paths;
  return resolved;
};
